import { rawSchema } from "src/data/commands";

import { JSONEditor } from "@json-editor/json-editor/dist/jsoneditor.js";

import { initJsonEditorInfoTextSupport } from "./jsonEditorInfoTextExtension";

import "./jsonEditor.css";

initJsonEditorInfoTextSupport();

export enum JsonEditorCommandType {
    Examples,
    ComplexCommands,
    Raw,
}

type EventEmittingEditor = { on: (event: string, handler: () => void) => void };

// json-editor still runs one validation pass during init even with `show_errors: "interaction"`,
// so the error message is rendered on first paint before the user has touched the field.
// hides initial error messages
const suppressInitialValidationErrors = (container: HTMLElement) => {
    container.querySelectorAll<HTMLElement>(".errmsg").forEach((el) => {
        el.style.display = "none";
    });
};

const hideErrorsOnReady = (editor: JSONEditor, container: HTMLElement) => {
    (editor as unknown as EventEmittingEditor).on("ready", () => suppressInitialValidationErrors(container));
};

export const updateEditor = (
    simpleEditor: JSONEditor | null,
    editorRef: React.RefObject<HTMLDivElement | null>,
    editorType: JsonEditorCommandType,
    commandsArr?: { name: string; schema: Record<string, unknown> }[],
    commandType?: string,
) => {
    if (simpleEditor) {
        simpleEditor.destroy();
    }

    if (!editorRef.current) {
        return null;
    }

    const container = editorRef.current;

    switch (editorType) {
        case JsonEditorCommandType.Examples: {
            const editor = new JSONEditor(container, {
                schema: commandsArr?.find((obj) => obj.name === commandType)?.schema,
                compact: true,
                disable_collapse: true,
                disable_edit_json: true,
                disable_properties: true,
                show_errors: "interaction",
                theme: "barebones",
            });
            hideErrorsOnReady(editor, container);
            return editor;
        }
        case JsonEditorCommandType.ComplexCommands: {
            const editor = new JSONEditor(container, {
                schema: commandsArr?.find((obj) => obj.name === commandType)?.schema,
                disable_collapse: true,
                disable_edit_json: true,
                show_errors: "interaction",
                theme: "barebones",
            });
            hideErrorsOnReady(editor, container);
            return editor;
        }
        case JsonEditorCommandType.Raw:
            return new JSONEditor(container, {
                schema: rawSchema,
                disable_edit_json: false,
                disable_collapse: true,
                show_errors: "always",
                theme: "barebones",
                iconlib: "fontawesome5",
            });
    }
};

type ValidatingEditor = {
    validate: (value?: unknown) => Array<Record<string, unknown>>;
    getValue: () => unknown;
};

// validate if editor is empty && parsable
// When `container` is passed, also un-hides any `.errmsg` nodes that were dimmed on init
// so the inline error reappears next to the offending field on submit.
export const validateEditor = (
    editor: { validate: () => Array<Record<string, unknown>> } | null,
    container?: HTMLElement | null,
) => {
    if (!editor) {
        return undefined;
    }

    if (container) {
        container.querySelectorAll<HTMLElement>(".errmsg").forEach((el) => {
            el.style.display = "";
        });
    }

    const typed = editor as ValidatingEditor;
    const errors = typed.validate(typed.getValue());
    if (errors?.length) {
        return errors;
    }
};
